import { useState, useRef, useEffect } from 'react'
import { useI18n } from '../../contexts/I18nContext'

interface LanguageSelectorProps {
  variant?: 'dropdown' | 'inline' | 'compact'
  showFlag?: boolean
  showNativeName?: boolean
}

export const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  variant = 'dropdown',
  showFlag = true,
  showNativeName = true,
}) => {
  const { language, languages, setLanguage, t } = useI18n()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const currentLanguage = languages.find((l) => l.code === language)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  if (variant === 'inline') {
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
        {languages.map((lang) => (
          <button
            key={lang.code}
            onClick={() => setLanguage(lang.code)}
            style={{
              padding: '8px 16px',
              backgroundColor: language === lang.code ? '#4A90E2' : 'white',
              color: language === lang.code ? 'white' : '#374151',
              border: `1px solid ${language === lang.code ? '#4A90E2' : '#D1D5DB'}`,
              borderRadius: '6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px',
              fontWeight: language === lang.code ? 500 : 400,
            }}
          >
            {showFlag && <span>{lang.flag}</span>}
            <span>{showNativeName ? lang.nativeName : lang.name}</span>
          </button>
        ))}
      </div>
    )
  }

  if (variant === 'compact') {
    return (
      <div ref={dropdownRef} style={{ position: 'relative' }}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          style={{
            padding: '6px 8px',
            backgroundColor: 'transparent',
            color: '#4B5563',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            fontSize: '14px',
          }}
          title={t('settings.language')}
        >
          {currentLanguage?.flag || '🌐'}
          <span style={{ fontSize: '10px' }}>▼</span>
        </button>

        {isOpen && (
          <div
            style={{
              position: 'absolute',
              top: '100%',
              right: 0,
              marginTop: '4px',
              backgroundColor: 'white',
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
              minWidth: '160px',
              zIndex: 1000,
            }}
          >
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => {
                  setLanguage(lang.code)
                  setIsOpen(false)
                }}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  backgroundColor: language === lang.code ? '#F3F4F6' : 'transparent',
                  color: '#374151',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '14px',
                  textAlign: 'left',
                }}
              >
                <span>{lang.flag}</span>
                <span>{lang.nativeName}</span>
                {language === lang.code && (
                  <span style={{ marginLeft: 'auto', color: '#4A90E2' }}>✓</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    )
  }

  // Default dropdown variant
  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          padding: '8px 12px',
          backgroundColor: 'white',
          color: '#374151',
          border: '1px solid #D1D5DB',
          borderRadius: '6px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '14px',
          minWidth: '150px',
        }}
      >
        {showFlag && <span>{currentLanguage?.flag}</span>}
        <span>{showNativeName ? currentLanguage?.nativeName : currentLanguage?.name}</span>
        <span style={{ marginLeft: 'auto', fontSize: '10px' }}>▼</span>
      </button>

      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            marginTop: '4px',
            backgroundColor: 'white',
            border: '1px solid #E5E7EB',
            borderRadius: '8px',
            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
            minWidth: '200px',
            zIndex: 1000,
          }}
        >
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => {
                setLanguage(lang.code)
                setIsOpen(false)
              }}
              style={{
                width: '100%',
                padding: '12px 16px',
                backgroundColor: language === lang.code ? '#F3F4F6' : 'transparent',
                color: '#374151',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                fontSize: '14px',
                textAlign: 'left',
              }}
            >
              <span style={{ fontSize: '20px' }}>{lang.flag}</span>
              <div>
                <div style={{ fontWeight: 500 }}>{lang.nativeName}</div>
                <div style={{ fontSize: '12px', color: '#6B7280' }}>{lang.name}</div>
              </div>
              {language === lang.code && (
                <span style={{ marginLeft: 'auto', color: '#4A90E2' }}>✓</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default LanguageSelector
